// AdministracionRed — pantalla con DATOS REALES (backend de CoopNet), no
// mock. Dos pestañas:
//   1. Estructura: Confederación → Federación → Cooperativa (ver y crear).
//   2. Usuarios y roles: asignar/revocar roles de CoopNet CON alcance (a
//      cuál cooperativa/federación/confederación aplica cada rol).
//
// La autorización real la hace el backend (RolesAdminController /
// OrganizacionController); aquí solo se muestra el mensaje si rechaza.
//
// Nota: solo aparecen en "Usuarios y roles" las personas que ya
// iniciaron sesión al menos una vez en CoopNet (así se crea su fila en
// usuarios_cache). Alguien nuevo debe entrar una vez antes de poder
// asignarle un rol.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CloudDownload as ImportIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { geografiaApi, organizacionApi, rolesAdminApi } from '../services/coopnetApi';
import EntidadDialog from '../components/EntidadDialog';
import CooperativaWizardModal from '../components/CooperativaWizardModal';
import { obtenerAfiliadosEcolab, obtenerEstructuraEcolab } from '../services/ecolabJerarquia';
import { useAuth } from '../context/AuthContext';

const ROLES = [
  'SuperAdmin',
  'AdminConfederacion',
  'AdminFederacion',
  'AdminCooperativa',
  'Contador',
  'RevisorFiscal',
  'Operario',
  'Afiliado',
  'RadicadorProyectos',
];

// A qué nivel se asigna cada rol (convención del backend, ver
// UsuarioCoopNetRol.cs): null = sin alcance (global).
const ALCANCE_POR_ROL = {
  SuperAdmin: null,
  AdminConfederacion: 'confederacion',
  AdminFederacion: 'federacion',
  AdminCooperativa: 'cooperativa',
  Contador: 'cooperativa',
  RevisorFiscal: 'cooperativa',
  Operario: 'cooperativa',
  Afiliado: 'cooperativa',
  RadicadorProyectos: 'cooperativa',
};

// ─────────────────────────────────────────────────────────────
// Pestaña 1 — Estructura
// ─────────────────────────────────────────────────────────────
const ColumnaLista = ({ titulo, items, seleccionado, onSeleccionar, onCrear, deshabilitarCrear, vacio, extraHeader, getSecundario }) => (
  <Card sx={{ flex: 1, minWidth: 260 }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {extraHeader}
          <Button size="small" startIcon={<AddIcon />} onClick={onCrear} disabled={deshabilitarCrear}>
            Nueva
          </Button>
        </Box>
      </Box>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {vacio}
        </Typography>
      ) : (
        <List dense>
          {items.map((it) => (
            <ListItemButton
              key={it.id}
              selected={seleccionado === it.id}
              onClick={() => onSeleccionar && onSeleccionar(it.id)}
            >
              <ListItemText primary={it.nombre} secondary={(getSecundario ? getSecundario(it) : it.especializacion || it.pais || it.nit) || null} />
            </ListItemButton>
          ))}
        </List>
      )}
    </CardContent>
  </Card>
);

const SIN_PADRE = '__sin_padre__';

const uneTextos = (...partes) => partes.filter(Boolean).join(' · ');

const Estructura = ({ internacionales, confederaciones, federaciones, cooperativas, recargar, onError }) => {
  const [selInt, setSelInt] = useState(null);
  const [selConf, setSelConf] = useState(null);
  const [selFed, setSelFed] = useState(null);
  const [selCoop, setSelCoop] = useState(null);
  const [wizard, setWizard] = useState(null); // { modo: 'crear'|'editar', cooperativa }
  const [entidad, setEntidad] = useState(null); // { nivel, modo, inicial }

  // Las confederaciones que aún no tienen internacional (anteriores a la migración 010) no
  // deben quedar invisibles: se agrupan aparte para poder enlazarlas editándolas.
  const huerfanas = confederaciones.filter((c) => !c.confederacionInternacionalId);
  const itemsInternacionales = [
    ...internacionales,
    ...(huerfanas.length
      ? [{ id: SIN_PADRE, nombre: '(Sin confederación internacional)', siglas: `${huerfanas.length} por enlazar` }]
      : []),
  ];

  const confVisibles = confederaciones.filter((c) =>
    selInt === SIN_PADRE ? !c.confederacionInternacionalId : c.confederacionInternacionalId === selInt
  );
  const fedVisibles = federaciones.filter((f) => f.confederacionId === selConf);
  const coopVisibles = cooperativas.filter((c) => c.federacionId === selFed);

  const intSel = internacionales.find((x) => x.id === selInt);
  const confSel = confVisibles.find((x) => x.id === selConf);
  const fedSel = fedVisibles.find((x) => x.id === selFed);
  const coopSel = coopVisibles.find((x) => x.id === selCoop);

  const botonEditar = (nivel, inicial) =>
    inicial && (
      <Button size="small" startIcon={<EditIcon />} onClick={() => setEntidad({ nivel, modo: 'editar', inicial })}>
        Editar
      </Button>
    );

  const padreInicial = selInt && selInt !== SIN_PADRE ? selInt : internacionales.length === 1 ? internacionales[0].id : null;

  const guardarEntidad = async (dto) => {
    const { nivel, modo, inicial } = entidad;
    onError('');
    if (nivel === 'internacional') {
      if (modo === 'crear') await organizacionApi.crearInternacional(dto);
      else await organizacionApi.actualizarInternacional(inicial.id, dto);
    } else if (nivel === 'confederacion') {
      if (modo === 'crear') await organizacionApi.crearConfederacion(dto);
      else await organizacionApi.actualizarConfederacion(inicial.id, dto);
    } else if (modo === 'crear') {
      await organizacionApi.crearFederacion(selConf, dto);
    } else {
      await organizacionApi.actualizarFederacion(inicial.id, dto);
    }
    setEntidad(null);
    await recargar();
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <ColumnaLista
          titulo="Confederación internacional"
          items={itemsInternacionales}
          seleccionado={selInt}
          onSeleccionar={(id) => {
            setSelInt(id);
            setSelConf(null);
            setSelFed(null);
            setSelCoop(null);
          }}
          getSecundario={(x) => uneTextos(x.siglas, x.activa === false && 'inactiva')}
          extraHeader={botonEditar('internacional', intSel)}
          onCrear={() => setEntidad({ nivel: 'internacional', modo: 'crear', inicial: null })}
          vacio="Aún no hay una confederación internacional. Crea la primera."
        />
        <ColumnaLista
          titulo="Confederaciones nacionales"
          items={confVisibles}
          seleccionado={selConf}
          onSeleccionar={(id) => {
            setSelConf(id);
            setSelFed(null);
            setSelCoop(null);
          }}
          getSecundario={(x) => uneTextos(x.pais, x.siglas, x.activa === false && 'inactiva')}
          extraHeader={botonEditar('confederacion', confSel)}
          onCrear={() => setEntidad({ nivel: 'confederacion', modo: 'crear', inicial: null })}
          deshabilitarCrear={internacionales.length === 0}
          vacio={
            internacionales.length === 0
              ? 'Crea primero la confederación internacional.'
              : selInt
              ? 'Esta confederación internacional aún no tiene confederaciones nacionales.'
              : 'Selecciona una confederación internacional.'
          }
        />
        <ColumnaLista
          titulo="Federaciones"
          items={fedVisibles}
          seleccionado={selFed}
          onSeleccionar={(id) => {
            setSelFed(id);
            setSelCoop(null);
          }}
          getSecundario={(x) => uneTextos(x.especializacion, x.siglas, x.activa === false && 'inactiva')}
          extraHeader={botonEditar('federacion', fedSel)}
          onCrear={() => setEntidad({ nivel: 'federacion', modo: 'crear', inicial: null })}
          deshabilitarCrear={!selConf}
          vacio={selConf ? 'Esta confederación aún no tiene federaciones.' : 'Selecciona una confederación nacional.'}
        />
        <ColumnaLista
          titulo="Cooperativas"
          items={coopVisibles}
          seleccionado={selCoop}
          onSeleccionar={setSelCoop}
          getSecundario={(c) =>
            `${c.nit || 'sin NIT'} · docs: ${(c.estadoDocumentos || '').replace(/_/g, ' ').toLowerCase()}${c.activa === false ? ' · inactiva' : ''}`
          }
          extraHeader={
            coopSel && (
              <Button size="small" startIcon={<EditIcon />} onClick={() => setWizard({ modo: 'editar', cooperativa: coopSel })}>
                Editar
              </Button>
            )
          }
          onCrear={() => setWizard({ modo: 'crear', cooperativa: null })}
          deshabilitarCrear={!selFed}
          vacio={selFed ? 'Esta federación aún no tiene cooperativas.' : 'Selecciona una federación.'}
        />
      </Box>

      <EntidadDialog
        open={Boolean(entidad)}
        nivel={entidad?.nivel || 'federacion'}
        modo={entidad?.modo || 'crear'}
        inicial={entidad?.inicial || null}
        internacionales={internacionales}
        padreInicial={padreInicial}
        onGuardar={guardarEntidad}
        onClose={() => setEntidad(null)}
      />

      <CooperativaWizardModal
        open={Boolean(wizard)}
        modo={wizard?.modo || 'crear'}
        federacionId={selFed}
        cooperativa={wizard?.cooperativa || null}
        onClose={() => setWizard(null)}
        onGuardado={() => recargar()}
      />
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Pestaña 2 — Usuarios y roles
// ─────────────────────────────────────────────────────────────
const UsuariosRoles = ({ confederaciones, federaciones, cooperativas, onError }) => {
  const [texto, setTexto] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [asignar, setAsignar] = useState(null); // { usuario, rol, confederacionId, federacionId, cooperativaId }

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      onError('');
      setUsuarios(await rolesAdminApi.listar(busqueda));
    } catch (e) {
      onError(e.message);
    } finally {
      setCargando(false);
    }
  }, [busqueda, onError]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const nombreAlcance = (r) => {
    if (r.cooperativaId) return cooperativas.find((x) => x.id === r.cooperativaId)?.nombre || 'Cooperativa';
    if (r.federacionId) return federaciones.find((x) => x.id === r.federacionId)?.nombre || 'Federación';
    if (r.confederacionId) return confederaciones.find((x) => x.id === r.confederacionId)?.nombre || 'Confederación';
    return null;
  };

  const revocar = async (r) => {
    if (!window.confirm(`¿Revocar el rol ${r.rol}${nombreAlcance(r) ? ` (${nombreAlcance(r)})` : ''}?`)) return;
    try {
      onError('');
      await rolesAdminApi.revocarRol(r.asignacionId);
      await cargar();
    } catch (e) {
      onError(e.message);
    }
  };

  const nivelRequerido = asignar ? ALCANCE_POR_ROL[asignar.rol] : null;
  const listaDelNivel =
    nivelRequerido === 'confederacion' ? confederaciones
    : nivelRequerido === 'federacion' ? federaciones
    : nivelRequerido === 'cooperativa' ? cooperativas
    : null;
  const alcanceCompleto =
    asignar &&
    (nivelRequerido === null ||
      (nivelRequerido === 'confederacion' && asignar.confederacionId) ||
      (nivelRequerido === 'federacion' && asignar.federacionId) ||
      (nivelRequerido === 'cooperativa' && asignar.cooperativaId));

  const confirmarAsignar = async () => {
    const alcance =
      nivelRequerido === 'confederacion'
        ? { confederacionId: asignar.confederacionId }
        : nivelRequerido === 'federacion'
        ? { federacionId: asignar.federacionId }
        : nivelRequerido === 'cooperativa'
        ? { cooperativaId: asignar.cooperativaId }
        : {};
    try {
      onError('');
      await rolesAdminApi.asignarRol(asignar.usuario.id, asignar.rol, alcance);
      setAsignar(null);
      await cargar();
    } catch (e) {
      onError(e.message);
    }
  };

  const selector = (label, valor, lista, onChange) => (
    <FormControl fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={valor || ''} onChange={(e) => onChange(e.target.value)}>
        {lista.map((x) => (
          <MenuItem key={x.id} value={x.id}>
            {x.nombre}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Aquí aparecen las personas que ya iniciaron sesión al menos una vez en CoopNet. Si alguien no aparece, pídele que
        entre una vez con su cuenta del ecosistema y vuelve a buscar.
      </Alert>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          size="small"
          label="Buscar por nombre, correo o documento"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setBusqueda(texto)}
          sx={{ minWidth: 320 }}
        />
        <Button variant="outlined" startIcon={<SearchIcon />} onClick={() => setBusqueda(texto)}>
          Buscar
        </Button>
      </Box>

      <Card>
        {cargando ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Roles asignados (y a qué entidad aplican)</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usuarios.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="text.secondary">
                      No hay usuarios para mostrar.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {usuarios.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600 }}>{u.nombre || '(sin nombre)'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {u.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {u.roles.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Sin roles
                      </Typography>
                    ) : (
                      u.roles.map((r) => (
                        <Chip
                          key={r.asignacionId}
                          sx={{ mr: 0.5, mb: 0.5 }}
                          label={nombreAlcance(r) ? `${r.rol} · ${nombreAlcance(r)}` : r.rol}
                          onDelete={() => revocar(r)}
                          deleteIcon={
                            <Tooltip title="Revocar">
                              <DeleteIcon />
                            </Tooltip>
                          }
                        />
                      ))
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      title="Asignar rol"
                      onClick={() => setAsignar({ usuario: u, rol: 'Contador', confederacionId: '', federacionId: '', cooperativaId: '' })}
                    >
                      <PersonAddIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={Boolean(asignar)} onClose={() => setAsignar(null)} fullWidth maxWidth="xs">
        {asignar && (
          <>
            <DialogTitle>Asignar rol a {asignar.usuario.nombre || asignar.usuario.email}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select
                  label="Rol"
                  value={asignar.rol}
                  onChange={(e) => setAsignar({ ...asignar, rol: e.target.value, confederacionId: '', federacionId: '', cooperativaId: '' })}
                >
                  {ROLES.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {nivelRequerido === 'confederacion' &&
                selector('Confederación', asignar.confederacionId, confederaciones, (v) => setAsignar({ ...asignar, confederacionId: v }))}
              {nivelRequerido === 'federacion' &&
                selector('Federación', asignar.federacionId, federaciones, (v) => setAsignar({ ...asignar, federacionId: v }))}
              {nivelRequerido === 'cooperativa' &&
                selector('Cooperativa', asignar.cooperativaId, cooperativas, (v) => setAsignar({ ...asignar, cooperativaId: v }))}
              {listaDelNivel && listaDelNivel.length === 0 && (
                <Alert severity="info">
                  Aún no hay {nivelRequerido === 'cooperativa' ? 'cooperativas' : nivelRequerido === 'federacion' ? 'federaciones' : 'confederaciones'} en
                  CoopNet. Créalas en la pestaña «Estructura» o, si ya existen en Fenagro, usa «Importar desde Ecolab» (arriba a la derecha).
                </Alert>
              )}
              {nivelRequerido === null && (
                <Alert severity="warning">SuperAdmin aplica a toda la red — no lleva entidad asociada.</Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAsignar(null)}>Cancelar</Button>
              <Button variant="contained" onClick={confirmarAsignar} disabled={!alcanceCompleto}>
                Asignar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Página
// ─────────────────────────────────────────────────────────────
const AdministracionRed = () => {
  const { user, recargarResumen } = useAuth();
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(true);
  const [internacionales, setInternacionales] = useState([]);
  const [confederaciones, setConfederaciones] = useState([]);
  const [federaciones, setFederaciones] = useState([]);
  const [cooperativas, setCooperativas] = useState([]);
  const [importacionHabilitada, setImportacionHabilitada] = useState(false);
  const [importando, setImportando] = useState(false);
  const [progreso, setProgreso] = useState('');
  const [resumen, setResumen] = useState('');

  const recargar = useCallback(async () => {
    try {
      const [i, c, f, k] = await Promise.all([
        organizacionApi.listarInternacionales(),
        organizacionApi.listarConfederaciones(),
        organizacionApi.listarFederaciones(),
        organizacionApi.listarCooperativas(),
      ]);
      setInternacionales(i);
      setConfederaciones(c);
      setFederaciones(f);
      setCooperativas(k);
      recargarResumen(); // los conteos del menú y de las pantallas cambian con la estructura
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [recargarResumen]);

  useEffect(() => {
    recargar();
    // La importación es TRANSITORIA: el backend la apaga el día del corte
    // (Ecolab:ImportacionHabilitada=false) y el botón desaparece solo.
    organizacionApi
      .estadoImportacionEcolab()
      .then((r) => setImportacionHabilitada(Boolean(r?.habilitada)))
      .catch(() => setImportacionHabilitada(false));
  }, [recargar]);

  const importar = async () => {
    setImportando(true);
    setError('');
    setProgreso('Leyendo la estructura de Ecolab…');
    try {
      // Lo lee el navegador (Ecolab bloquea servidor-a-servidor) y se lo entrega a CoopNet.
      const estructura = await obtenerEstructuraEcolab();
      // Si Ecolab solo trajo el NOMBRE del país de una confederación, se busca su id en el catálogo de geografía.
      const norm = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
      const paises = await geografiaApi.paises(true).catch(() => []);
      const idPorNombre = new Map((Array.isArray(paises) ? paises : []).map((p) => [norm(p.nombre), p.id]));
      const confNacionales = estructura.confNacionales.map((c) => ({
        ...c,
        paisId: c.paisId ?? idPorNombre.get(norm(c.pais)) ?? null,
      }));

      // Afiliados ACTIVOS de cada cooperativa (Ecolab los entrega por cooperativa).
      const lectura = await obtenerAfiliadosEcolab(
        estructura.cooperativas.map((c) => c.cooperativaID),
        setProgreso
      );

      setProgreso('Guardando en CoopNet…');
      const r = await organizacionApi.importarEcolab({ ...estructura, confNacionales, afiliados: lectura.afiliados });

      const actualizadas =
        r.internacionalesActualizadas + r.confederacionesActualizadas + r.federacionesActualizadas + r.cooperativasActualizadas;
      const desactivadas = r.confederacionesDesactivadas + r.federacionesDesactivadas + r.cooperativasDesactivadas;
      const avisos = [];
      if (lectura.cooperativasSinLeer > 0) avisos.push(`no se pudieron leer los afiliados de ${lectura.cooperativasSinLeer} cooperativa(s)`);
      if (lectura.detallesFallidos > 0) avisos.push(`${lectura.detallesFallidos} afiliado(s) se importaron solo con su resumen`);
      if (r.afiliadosOmitidos > 0) avisos.push(`${r.afiliadosOmitidos} afiliado(s) omitidos (sin cooperativa importada, sin documento o documento repetido)`);
      if (r.tercerosOmitidos > 0) avisos.push(`${r.tercerosOmitidos} afiliado(s) sin tercero contable porque su documento ya lo usa otro tercero`);
      setResumen(
        `Importado desde Ecolab: ${r.internacionalesCreadas} confederación internacional, ${r.confederacionesCreadas} confederaciones, ` +
          `${r.federacionesCreadas} federaciones, ${r.cooperativasCreadas} cooperativas y ${r.afiliadosCreados} afiliados nuevos ` +
          `(${actualizadas + r.afiliadosActualizados} ya importados actualizados${desactivadas ? `, ${desactivadas} desactivados` : ''}).` +
          (avisos.length ? ` Atención: ${avisos.join('; ')}.` : '')
      );
      await recargar();
    } catch (e) {
      setError(e.message);
    } finally {
      setImportando(false);
      setProgreso('');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Administración de la Red
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Estructura organizacional y asignación de roles — datos reales del sistema.
          </Typography>
        </Box>
        {importacionHabilitada && user?.esSuperAdmin && (
          <Tooltip title="Transitorio: trae a CoopNet la estructura y los afiliados ACTIVOS que hoy están en el portal de Fenagro (Ecolab). No borra nada ni pisa lo que ya completaste aquí.">
            <span>
              <Button
                variant="outlined"
                startIcon={importando ? <CircularProgress size={18} /> : <ImportIcon />}
                onClick={importar}
                disabled={importando}
              >
                {importando && progreso ? progreso : 'Importar desde Ecolab'}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {resumen && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResumen('')}>
          {resumen}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Estructura" />
        <Tab label="Usuarios y roles" />
      </Tabs>

      {cargando ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : tab === 0 ? (
        <Estructura
          internacionales={internacionales}
          confederaciones={confederaciones}
          federaciones={federaciones}
          cooperativas={cooperativas}
          recargar={recargar}
          onError={setError}
        />
      ) : (
        <UsuariosRoles
          confederaciones={confederaciones}
          federaciones={federaciones}
          cooperativas={cooperativas}
          onError={setError}
        />
      )}
    </Box>
  );
};

export default AdministracionRed;
